import { AiConfigKey, AiGeneratorAiConfig, RespondentEditable } from '../../../types';

/**
 * The AI Generator field's configurable knobs (tone, goal, persona, type,
 * model, language), sourced the same way the CRM's own AI generator sources
 * them: local option lists here, personas fetched from the CRM at runtime
 * (see services/api/fabaiApi.ts). The author picks defaults on the field;
 * the visitor may override them when the field allows it.
 */

export interface AiTypeOption {
  value: string;
  label: string;
  /** What the AI service expects as the request's `source` for this `type`. */
  source: string;
}

export const TONES: string[] = [
  'Analytical',
  'Assertive',
  'Authoritative',
  'Challenging',
  'Conversational',
  'Curious',
  'Educational',
  'Empathetic',
  'Friendly',
  'Funny',
  'Informative',
  'Inspirational',
  'Motivational',
  'Narrative',
  'Normal',
  'Persuasive',
  'Professional',
  'Reflective',
  'Sincere',
  'Visionary',
];

export const AI_TYPES: AiTypeOption[] = [
  { value: 'linkedin_create_post', label: 'LinkedIn — Create post', source: 'linkedin' },
  { value: 'linkedin_generate_post_ideas', label: 'LinkedIn — Post ideas', source: 'linkedin' },
  { value: 'linkedin_create_article', label: 'LinkedIn — Write an article', source: 'linkedin' },
  { value: 'linkedin_post_comment', label: 'LinkedIn — Comment on a post', source: 'linkedin' },
  { value: 'linkedin_article_comment', label: 'LinkedIn — Comment on an article', source: 'linkedin' },
  { value: 'linkedin_invitation_note', label: 'LinkedIn — Invitation note', source: 'linkedin' },
  { value: 'linkedin_profile_headline', label: 'LinkedIn — Profile headline', source: 'linkedin' },
  { value: 'linkedin_profile_about', label: 'LinkedIn — Profile "About"', source: 'linkedin' },
  { value: 'linkedin_message_reply', label: 'LinkedIn — Message reply', source: 'linkedin' },
  { value: 'upwork_proposal_coverletter', label: 'Upwork — Proposal cover letter', source: 'upwork' },
  { value: 'instagram_create_post', label: 'Instagram — Create post', source: 'instagram' },
  { value: 'instagram_post_comment', label: 'Instagram — Comment on a post', source: 'instagram' },
  { value: 'facebook_create_post', label: 'Facebook — Create post', source: 'facebook' },
  { value: 'facebook_post_comment', label: 'Facebook — Comment on a post', source: 'facebook' },
  { value: 'twitter_create_post', label: 'Twitter / X — Create post', source: 'twitter' },
  { value: 'twitter_post_comment', label: 'Twitter / X — Reply to a post', source: 'twitter' },
  { value: 'quora_create_answer', label: 'Quora — Answer a question', source: 'quora' },
  { value: 'quora_create_post', label: 'Quora — Create post', source: 'quora' },
  { value: 'reddit_create_post', label: 'Reddit — Create post', source: 'reddit' },
  { value: 'reddit_post_comment', label: 'Reddit — Comment on a post', source: 'reddit' },
];

const SHARED_GOALS = [
  'Brand Building',
  'Lead Generation',
  'Marketing Services',
  'Networking Engagement',
  'Thought Leadership',
];

const INVITATION_NOTE_GOALS = ['Networking'];

const POSTING_GOALS = [
  'Ask a Question',
  'Celebrating Others',
  'Challenges and Solutions',
  'Common Myths',
  'Company Updates',
  'Educational',
  'Encourage Engagement',
  'Informational',
  'Introduction Post',
  'Networking',
  'Personal Story',
  'Poll',
  'Promotional',
  'Provide Value',
  'Seeking Advice',
  'Success Stories',
  'Thought Leadership',
];

const PROFILE_GOALS = [
  'Thought Leadership',
  'Lead Generation',
  'Brand Building',
  'Networking Engagement',
  'Marketing Services',
];

const COMMENT_GOALS = [
  'Build Rapport',
  'Build Relationships',
  'Encourage Action',
  'Encourage Debate',
  'Encourage Engagement',
  'Encourage Sharing',
  'Establish Expertise',
  'Highlight Author',
  'Highlight Partnerships',
  'Offer Support',
  'Offer Tips',
  'Promote Product',
  'Provide Feedback',
  'Provide Resources',
  'Provide Value',
  'Reinforce Branding',
  'Respond to Criticism',
  'Share Anecdotes',
  'Share News',
  'Share Opinion',
  'Share Stories',
  'Show Empathy',
  'Showcase Values',
  'Showcase Voice',
  'Spark Curiosity',
  'Use Inclusive Language',
];

const SALES_GOALS = ['Offer Support', 'Provide Value', 'Marketing Services', 'Lead Generation'];

/** The goal list a `type` offers — same grouping the CRM's AI generator uses. */
export const getApplicableGoals = (type: string): string[] => {
  if (type.includes('invitation_note')) return INVITATION_NOTE_GOALS;
  if (type.includes('proposal') || type.includes('coverletter')) return SALES_GOALS;
  if (type.includes('post_comment') || type.includes('article_comment') || type.includes('message_reply')) {
    return COMMENT_GOALS;
  }
  if (type.includes('profile_headline') || type.includes('profile_about')) return PROFILE_GOALS;
  if (type.includes('post_ideas') || type.endsWith('create_post') || type.includes('create_article') || type.includes('create_answer')) {
    return POSTING_GOALS;
  }
  return SHARED_GOALS;
};

/** The `source` a `type` is sent with; unknown types fall back to `unknown`. */
export const getSourceForType = (type: string): string =>
  AI_TYPES.find((option) => option.value === type)?.source || 'unknown';

export const MODELS: { value: string; label: string }[] = [
  { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
  { value: 'gpt-4', label: 'GPT-4' },
  { value: 'gpt-3.5', label: 'GPT-3.5' },
  { value: 'claude-3', label: 'Claude 3' },
  { value: 'gemini', label: 'Gemini' },
];

export const LANGUAGES: { value: string; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'de', label: 'German' },
  { value: 'jp', label: 'Japanese' },
];

/**
 * What every request used before the field became configurable — kept as the
 * default so existing forms generate exactly what they always did.
 */
export const DEFAULT_AI_CONFIG: AiGeneratorAiConfig = {
  type: 'linkedin_create_post',
  tone: 'Professional',
  goal: 'Thought Leadership',
  persona: '',
  model: 'gpt-4',
  language: 'en',
};

/**
 * A field's saved config merged over the defaults, so a field saved before a
 * key existed (or with a typo'd value) still produces a complete request.
 * A saved goal outside the current type's list is kept, not dropped — it was
 * chosen deliberately and the select simply shows it alongside the list.
 */
export const resolveAiConfig = (config?: Partial<AiGeneratorAiConfig> | null): AiGeneratorAiConfig => ({
  ...DEFAULT_AI_CONFIG,
  ...(config || {}),
});

/** Every setting key, in the order the editor and the settings strip show them. */
export const AI_CONFIG_KEYS: AiConfigKey[] = ['type', 'tone', 'goal', 'persona', 'model', 'language'];

/**
 * Reserved answers-key prefix an AiGenerator field uses to publish its effective
 * config (author defaults + the respondent's chip edits) alongside its output.
 * The server strips these before saving (they are not answers) and feeds them to
 * workflow variables bound to this field. Mirrors `AI_OVERRIDE_PREFIX` in
 * cs-app-form-builder src/services/workflow/workflowSettings.ts.
 */
export const AI_OVERRIDE_PREFIX = '_aiCfg:';

/** Human labels for the keys, used by the editor's per-setting toggles. */
export const AI_CONFIG_LABELS: Record<AiConfigKey, string> = {
  type: 'Type',
  tone: 'Tone',
  goal: 'Goal',
  persona: 'Persona',
  model: 'Model',
  language: 'Language',
};

/**
 * Which settings the respondent may change. Fields saved before per-setting
 * toggles existed carried a single `respondentConfigurable` boolean — when it
 * was on, every setting stays editable so those forms keep their behaviour.
 */
export const resolveRespondentEditable = (data?: {
  respondentEditable?: RespondentEditable;
  respondentConfigurable?: boolean;
}): Record<AiConfigKey, boolean> => {
  const perKey = data?.respondentEditable;
  const legacyAll = data?.respondentConfigurable === true;
  return AI_CONFIG_KEYS.reduce(
    (acc, key) => {
      acc[key] = perKey?.[key] ?? legacyAll;
      return acc;
    },
    {} as Record<AiConfigKey, boolean>,
  );
};
